import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import ScienceIcon from '@mui/icons-material/Science';
import TestRunDialog from '../EmailMarketing/flowbuilder/TestRunDialog';
import { isStagingBuild } from '../../config/featureFlags';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import useEmailMarketing from '../../hooks/useEmailMarketing';
import { BRAND_PRIMARY_MAIN, BRAND_SECONDARY_MAIN } from '../../theme/managementTheme';
import useFlowGraph from '../EmailMarketing/flowbuilder/useFlowGraph';
import { BLOCK_TYPES, FLOW_STATUS_META } from '../EmailMarketing/flowbuilder/blockCatalog';
import {
  BLOCK_WIDTH,
  connectionKey,
  findFreePosition,
  makeBlockId,
} from '../EmailMarketing/flowbuilder/flowGeometry';
import FlowCanvas from '../EmailMarketing/flowbuilder/FlowCanvas';
import BlockPalette from '../EmailMarketing/flowbuilder/BlockPalette';
import BlockPropertiesPanel from '../EmailMarketing/flowbuilder/BlockPropertiesPanel';
import FlowSettingsDialog from '../EmailMarketing/flowbuilder/FlowSettingsDialog';
import LeaveFlowDialog from '../EmailMarketing/flowbuilder/LeaveFlowDialog';
import ValidationStatusChip from '../EmailMarketing/flowbuilder/ValidationStatusChip';

const asArray = (value, key) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value[key])) return value[key];
  return [];
};

const isEditableTarget = target => {
  if (!target || !target.tagName) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

// Extracts the validation issues array from a 422 activation error.
const issuesFromError = error => {
  const data = error?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.issues)) return data.issues;
  return [];
};

// Drag & drop automation canvas for a single e-mail marketing flow
// (GetResponse-like): block palette, connections, properties, validation,
// interim saves ("Aktualisieren") and activation via the settings dialog.
const EmailMarketingFlowBuilder = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const authCtx = useAuth();
  const { getFlowById, updateFlow, validateFlow, activateFlow, pauseFlow, getLists } =
    useEmailMarketing();
  const {
    blocks,
    connections,
    graphRef,
    setGraph,
    resetGraph,
    beginInteraction,
    undo,
    redo,
    canUndo,
    canRedo,
    dirty: graphDirty,
    setDirty: setGraphDirty,
  } = useFlowGraph();

  const [flowMeta, setFlowMeta] = useState(null);
  const [flowName, setFlowName] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [lists, setLists] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [selectedConnectionKey, setSelectedConnectionKey] = useState(null);
  const [sidebarTab, setSidebarTab] = useState('elements');
  const [validation, setValidation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [testRunOpen, setTestRunOpen] = useState(false);
  // Build-time constant: on a live build the button is not hidden but absent.
  const stagingBuild = isStagingBuild();
  const [activationIssues, setActivationIssues] = useState([]);
  const [activationBusy, setActivationBusy] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const canvasApiRef = useRef(null);

  const canRead = authCtx.checkRight('emailMarketing:read');
  const canEdit = authCtx.checkRight('emailMarketing:update');
  const canActivate = authCtx.checkRight('emailMarketing:activate');
  const dirty = graphDirty || nameDirty;

  /* ------------------------------ data loading ------------------------------ */

  useEffect(() => {
    if (!canRead) return undefined;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [flowResult, listsResult] = await Promise.all([getFlowById(flowId), getLists()]);
        if (!active) return;
        const flowDoc = flowResult?.flow || flowResult;
        if (flowDoc && flowDoc._id) {
          setFlowMeta(flowDoc);
          setFlowName(flowDoc.name || '');
          setNameDirty(false);
          resetGraph({
            blocks: Array.isArray(flowDoc.blocks) ? flowDoc.blocks : [],
            connections: Array.isArray(flowDoc.connections) ? flowDoc.connections : [],
          });
        }
        setLists(asArray(listsResult, 'lists'));
      } catch {
        if (active) toast.error('Fehler beim Laden des Flows');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, canRead, loadAttempt]);

  const refreshLists = useCallback(async () => {
    try {
      const result = await getLists();
      if (result !== null) setLists(asArray(result, 'lists'));
    } catch {
      toast.error('Fehler beim Aktualisieren der Listen');
    }
  }, [getLists]);

  /* ------------------------------ persistence ------------------------------ */

  const persistFlow = useCallback(
    async ({ silent = false, revalidate = true } = {}) => {
      setSaving(true);
      try {
        const { blocks: currentBlocks, connections: currentConnections } = graphRef.current;
        const trimmedName = flowName.trim();
        const payload = { blocks: currentBlocks, connections: currentConnections };
        if (trimmedName) payload.name = trimmedName;
        const result = await updateFlow(flowId, payload);
        // null = request stored for re-authentication: no data, no error.
        if (result === null) return false;
        setGraphDirty(false);
        setNameDirty(false);
        const doc = result?.flow || result;
        if (doc && doc._id) setFlowMeta(doc);
        if (!silent) toast.success('Flow gespeichert');
        if (revalidate) {
          try {
            const validationResult = await validateFlow(flowId);
            if (validationResult !== null) setValidation(validationResult);
          } catch {
            // Validation is informational only; saving already succeeded.
          }
        }
        return true;
      } catch {
        toast.error('Fehler beim Speichern des Flows');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [flowId, flowName, graphRef, setGraphDirty, updateFlow, validateFlow]
  );

  /* ----------------------------- graph mutations ----------------------------- */

  const selectBlock = useCallback(blockId => {
    setSelectedBlockId(blockId);
    setSelectedConnectionKey(null);
    setSidebarTab('properties');
  }, []);

  const selectConnection = useCallback(key => {
    setSelectedConnectionKey(key);
    setSelectedBlockId(null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedBlockId(null);
    setSelectedConnectionKey(null);
  }, []);

  const handleAddBlockAt = useCallback(
    (type, position) => {
      if (!canEdit) return;
      const def = BLOCK_TYPES[type];
      if (!def) return;
      if (def.isStart && graphRef.current.blocks.some(block => BLOCK_TYPES[block.type]?.isStart)) {
        toast.warning('Es darf nur einen Startblock geben');
        return;
      }
      const block = { blockId: makeBlockId(), type, position, config: def.defaultConfig() };
      setGraph(graph => ({ ...graph, blocks: [...graph.blocks, block] }));
      selectBlock(block.blockId);
    },
    [canEdit, graphRef, selectBlock, setGraph]
  );

  const handleAddBlockClick = useCallback(
    type => {
      const center = canvasApiRef.current?.getViewportCenterCanvasPos() || { x: 200, y: 150 };
      const start = { x: Math.round(center.x - BLOCK_WIDTH / 2), y: Math.round(center.y - 60) };
      handleAddBlockAt(type, findFreePosition(graphRef.current.blocks, start));
    },
    [graphRef, handleAddBlockAt]
  );

  const updateBlockConfig = useCallback(
    (blockId, partial) => {
      setGraph(graph => ({
        ...graph,
        blocks: graph.blocks.map(block =>
          block.blockId === blockId ? { ...block, config: { ...block.config, ...partial } } : block
        ),
      }));
    },
    [setGraph]
  );

  const deleteBlock = useCallback(
    blockId => {
      setGraph(graph => ({
        blocks: graph.blocks.filter(block => block.blockId !== blockId),
        connections: graph.connections.filter(
          connection => connection.from !== blockId && connection.to !== blockId
        ),
      }));
      setSelectedBlockId(previous => (previous === blockId ? null : previous));
    },
    [setGraph]
  );

  const deleteConnectionByKey = useCallback(
    key => {
      setGraph(graph => ({
        ...graph,
        connections: graph.connections.filter(connection => connectionKey(connection) !== key),
      }));
      setSelectedConnectionKey(previous => (previous === key ? null : previous));
    },
    [setGraph]
  );

  const handleConnect = useCallback(
    (from, to, branch = null) => {
      const connection = branch ? { from, to, branch } : { from, to };
      setGraph(graph => ({ ...graph, connections: [...graph.connections, connection] }));
    },
    [setGraph]
  );

  /* ------------------------- keyboard & unload guards ------------------------- */

  useEffect(() => {
    const handleKeyDown = event => {
      if (isEditableTarget(event.target) || settingsOpen || leaveDialogOpen || !canEdit) return;
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedConnectionKey) {
          event.preventDefault();
          deleteConnectionByKey(selectedConnectionKey);
        } else if (selectedBlockId) {
          event.preventDefault();
          deleteBlock(selectedBlockId);
        }
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    canEdit,
    deleteBlock,
    deleteConnectionByKey,
    leaveDialogOpen,
    redo,
    selectedBlockId,
    selectedConnectionKey,
    settingsOpen,
    undo,
  ]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  /* ------------------------- activation & navigation ------------------------- */

  const focusIssue = useCallback(
    issue => {
      if (!issue?.blockId) return;
      setSettingsOpen(false);
      selectBlock(issue.blockId);
      canvasApiRef.current?.centerOnBlock(issue.blockId);
    },
    [selectBlock]
  );

  const handleActivate = useCallback(async () => {
    setActivationBusy(true);
    try {
      if (dirty && canEdit) {
        const saved = await persistFlow({ silent: true, revalidate: false });
        if (!saved) return;
      }
      const validationResult = await validateFlow(flowId);
      if (validationResult === null) return;
      if (!validationResult.valid) {
        const issues = validationResult.issues || [];
        setActivationIssues(issues);
        setValidation(validationResult);
        return;
      }
      const result = await activateFlow(flowId);
      if (result === null) return;
      const doc = result?.flow || result;
      if (doc && doc._id) setFlowMeta(doc);
      else setFlowMeta(previous => ({ ...(previous || {}), status: 'active' }));
      setActivationIssues([]);
      setValidation(validationResult);
      setSettingsOpen(false);
      toast.success('Flow ist jetzt aktiv');
    } catch (error) {
      if (error?.statusCode === 422) {
        const issues = issuesFromError(error);
        setActivationIssues(issues);
        setValidation({ valid: false, issues });
      } else {
        toast.error('Fehler beim Aktivieren des Flows');
      }
    } finally {
      setActivationBusy(false);
    }
  }, [activateFlow, canEdit, dirty, flowId, persistFlow, validateFlow]);

  const handlePause = useCallback(async () => {
    setActivationBusy(true);
    try {
      const result = await pauseFlow(flowId);
      if (result === null) return;
      const doc = result?.flow || result;
      if (doc && doc._id) setFlowMeta(doc);
      else setFlowMeta(previous => ({ ...(previous || {}), status: 'paused' }));
      setSettingsOpen(false);
      toast.success('Flow pausiert');
    } catch {
      toast.error('Fehler beim Pausieren des Flows');
    } finally {
      setActivationBusy(false);
    }
  }, [flowId, pauseFlow]);

  // Persists flow-level behaviour settings (test mode, re-entry, conversion
  // goal) from the settings dialog.
  const handleSaveSettings = useCallback(
    async settings => {
      try {
        const result = await updateFlow(flowId, { settings });
        if (result === null) return;
        const doc = result?.flow || result;
        if (doc && doc._id) setFlowMeta(doc);
        else setFlowMeta(previous => ({ ...(previous || {}), settings }));
        toast.success('Flow-Einstellungen gespeichert.');
      } catch (err) {
        if (err?.code === 'MARKETING_FLOW_SETTINGS_INVALID') {
          const detail = Array.isArray(err?.data)
            ? err.data.map(issue => issue.message).join(' ')
            : '';
          toast.error(`Die Flow-Einstellungen sind ungültig. ${detail}`.trim());
        } else {
          toast.error('Fehler beim Speichern der Flow-Einstellungen');
        }
        throw err;
      }
    },
    [flowId, updateFlow]
  );

  const handleLeave = useCallback(() => {
    if (dirty) setLeaveDialogOpen(true);
    else navigate('/emailMarketing');
  }, [dirty, navigate]);

  const handleSaveAndLeave = useCallback(async () => {
    const saved = await persistFlow({ silent: true, revalidate: false });
    if (saved) navigate('/emailMarketing');
    else setLeaveDialogOpen(false);
  }, [navigate, persistFlow]);

  /* --------------------------------- render --------------------------------- */

  const selectedBlock = useMemo(
    () => blocks.find(block => block.blockId === selectedBlockId) || null,
    [blocks, selectedBlockId]
  );
  const hasStartBlock = useMemo(
    () => blocks.some(block => BLOCK_TYPES[block.type]?.isStart),
    [blocks]
  );
  const statusMeta = FLOW_STATUS_META[flowMeta?.status] || FLOW_STATUS_META.draft;

  if (!authCtx.checkRight('emailMarketing:read')) {
    return <Navigate to='/welcome' replace />;
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!flowMeta) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          color: 'text.primary',
        }}
      >
        <Typography variant='h6'>Flow konnte nicht geladen werden.</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant='outlined' onClick={() => setLoadAttempt(attempt => attempt + 1)}>
            Erneut versuchen
          </Button>
          <Button variant='contained' onClick={() => navigate('/emailMarketing')}>
            Zur Übersicht
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        color: 'text.primary',
      }}
    >
      {/* Top toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Tooltip title='Rückgängig'>
          <span>
            <IconButton size='small' aria-label='Rückgängig' disabled={!canEdit || !canUndo} onClick={undo}>
              <UndoIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title='Wiederholen'>
          <span>
            <IconButton size='small' aria-label='Wiederholen' disabled={!canEdit || !canRedo} onClick={redo}>
              <RedoIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title='Zurück zur E-Mail-Marketing Übersicht'>
          <IconButton
            aria-label='Zurück zur Übersicht'
            onClick={handleLeave}
            sx={{
              bgcolor: BRAND_PRIMARY_MAIN,
              color: BRAND_SECONDARY_MAIN,
              borderRadius: 2,
              width: 40,
              height: 40,
              '&:hover': { bgcolor: '#e6d400' },
            }}
          >
            <EmailIcon />
          </IconButton>
        </Tooltip>
        <InputBase
          value={flowName}
          onChange={event => {
            setFlowName(event.target.value);
            setNameDirty(true);
          }}
          placeholder='Flow-Name'
          disabled={!canEdit}
          inputProps={{ 'aria-label': 'Flow-Name' }}
          sx={{
            typography: 'h6',
            fontWeight: 600,
            px: 1,
            borderRadius: 1,
            minWidth: 140,
            maxWidth: 320,
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-focused': { bgcolor: 'action.hover' },
          }}
        />
        <Chip size='small' label={statusMeta.label} color={statusMeta.color} />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <ValidationStatusChip validation={validation} onIssueClick={focusIssue} />
        </Box>
        <Badge color='warning' variant='dot' invisible={!dirty}>
          <Tooltip title={canEdit ? 'Zwischenstand speichern' : 'Du benötigst das Recht "emailMarketing:update".'}>
            <span>
              <Button
                variant='contained'
                size='small'
                startIcon={<SaveIcon />}
                disabled={!canEdit || saving}
                onClick={() => persistFlow()}
              >
                Aktualisieren
              </Button>
            </span>
          </Tooltip>
        </Badge>
        {stagingBuild && (
          <Tooltip title='Sofort-Testlauf (nur Staging)'>
            <IconButton aria-label='Sofort-Testlauf' onClick={() => setTestRunOpen(true)}>
              <ScienceIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title='Flow-Einstellungen'>
          <IconButton aria-label='Flow-Einstellungen' onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Canvas + sidebar */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <FlowCanvas
          ref={canvasApiRef}
          blocks={blocks}
          connections={connections}
          lists={lists}
          selectedBlockId={selectedBlockId}
          selectedConnectionKey={selectedConnectionKey}
          readOnly={!canEdit}
          onSelectBlock={selectBlock}
          onSelectConnection={selectConnection}
          onClearSelection={clearSelection}
          setGraph={setGraph}
          beginInteraction={beginInteraction}
          onConnect={handleConnect}
          onDeleteBlock={deleteBlock}
          onDeleteConnection={deleteConnectionByKey}
          onAddBlockAt={handleAddBlockAt}
        />
        <Box
          sx={{
            width: 300,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Tabs
            value={sidebarTab}
            onChange={(event, value) => setSidebarTab(value)}
            variant='fullWidth'
            sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}`, flexShrink: 0 }}
          >
            <Tab value='elements' label='Elemente' />
            <Tab value='properties' label='Eigenschaften' />
          </Tabs>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {sidebarTab === 'elements' ? (
              <BlockPalette hasStartBlock={hasStartBlock} readOnly={!canEdit} onAdd={handleAddBlockClick} />
            ) : (
              <BlockPropertiesPanel
                block={selectedBlock}
                blocks={blocks}
                flowId={flowMeta?._id}
                flowStatus={flowMeta?.status}
                lists={lists}
                readOnly={!canEdit}
                onConfigChange={updateBlockConfig}
                onListsChanged={refreshLists}
              />
            )}
          </Box>
        </Box>
      </Box>

      <FlowSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        flowMeta={flowMeta}
        canActivate={canActivate}
        canEdit={canEdit}
        busy={activationBusy}
        issues={activationIssues}
        onActivate={handleActivate}
        onPause={handlePause}
        onIssueClick={focusIssue}
        onSaveSettings={handleSaveSettings}
      />
      {stagingBuild && flowMeta?._id && (
        <TestRunDialog
          open={testRunOpen}
          onClose={() => setTestRunOpen(false)}
          flowId={flowMeta._id}
          flowName={flowMeta.name || 'Flow'}
          suggestedEmails={flowMeta.settings?.testMode?.allowedEmails || []}
        />
      )}
      <LeaveFlowDialog
        open={leaveDialogOpen}
        busy={saving}
        onSaveAndLeave={handleSaveAndLeave}
        onDiscard={() => navigate('/emailMarketing')}
        onCancel={() => setLeaveDialogOpen(false)}
      />
    </Box>
  );
};

export default EmailMarketingFlowBuilder;
